import isNil from 'lodash/isNil';
import {useMemo} from 'react';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import useTypedSelector from '../../../../common/useTypedSelector';
import {
  ContinueInfo,
  ContinueProductType,
  SlideType,
} from '../../../../graphql/generated/types';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';

export function useContentMainTicketActionInfo({
  seriesId,
  isInPreviewTab,
}: {
  seriesId: number;
  isInPreviewTab?: boolean;
}) {
  const {isMobile} = requestContext.requestInfo.display;
  const data = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data
    ?.contentHomeOverview;

  const continueData = data?.content?.serviceProperty?.pageContinue;
  // PC 환경에서는 preview 데이터가 존재하더라도 무시합니다.
  const preview = isMobile
    ? data?.content?.serviceProperty?.preview
    : undefined;
  const previewProductId = preview?.item?.productId;
  const previewReadType = preview?.item?.slideType;
  const relatedSeries = data?.relatedSeries;

  const isDone = useTypedSelector(state => {
    return previewProductId
      ? previewReadType === SlideType.Epub
        ? !isNil(
            state.textViewer.viewData[previewProductId]?.currentChapterId
          ) &&
          !isNil(state.textViewer.viewData[previewProductId]?.currentPage) &&
          state.textViewer.viewData[previewProductId]?.currentChapterId ===
            (state.textViewer.viewData[previewProductId]?.chapterCount || 0) -
              1 &&
          state.textViewer.viewData[previewProductId]?.currentPage ===
            state.textViewer.viewData[previewProductId]?.currentPageCount
        : !isNil(state.imageViewer.viewData[previewProductId]?.currentPage) &&
          state.imageViewer.viewData[previewProductId]?.currentPage ===
            state.imageViewer.viewData[previewProductId]?.totalPage
      : false;
  });

  const ticketActionInfo = useMemo(() => {
    if (!preview && !continueData) {
      return undefined;
    }

    if (preview && !continueData?.lastReadProductId) {
      const {item, nextItem} = preview || {};
      const showNextItem = (!!nextItem && isDone) || false;

      return {
        productId: showNextItem ? nextItem?.productId : item?.productId,
        title: showNextItem ? nextItem?.title : item?.title,
        hasUnreadSingle: continueData?.hasUnreadSingle,
        relatedSeries,
        buttonTitle: showNextItem
          ? t('contenthome_next_ep_button')
          : isInPreviewTab
            ? t('contenthome_first_ep_tab_viewer')
            : t('contenthome_first_ep_tab_button'),
        copy: showNextItem
          ? '다음화 보기'
          : isInPreviewTab
            ? '뷰어로 첫화보기'
            : '첫편보기',
        shouldShowTitle: showNextItem,
        isFree: showNextItem ? !!nextItem?.isFree : !!item?.isFree,
        pickExactly: showNextItem,
        isDone,
        hasLastReadHistory: false,
      };
    } else {
      const {continueProductType, title, productId, isFree} =
        continueData as ContinueInfo;

      return {
        productId,
        title,
        hasUnreadSingle: continueData?.hasUnreadSingle,
        relatedSeries,
        buttonTitle:
          continueProductType === ContinueProductType.First
            ? t('contenthome_first_ep_tab_button')
            : continueProductType === ContinueProductType.Last
              ? t('contenthome_continue_ep_button')
              : t('contenthome_next_ep_button'),
        copy:
          continueProductType === ContinueProductType.First
            ? '첫편보기'
            : continueProductType === ContinueProductType.Last
              ? '이어보기'
              : '다음화 보기',
        shouldShowTitle: continueProductType !== ContinueProductType.First,
        isFree:
          (continueProductType === ContinueProductType.First && isFree) ||
          false,
        pickExactly: continueProductType === ContinueProductType.Next, // 다음화 보기만 명확하게 선택한 것으로 보고, 그게 아닌 경우 모두 애매한 상황으로 본다
        isDone,
        hasLastReadHistory: !!continueData?.lastReadProductId,
      };
    }
  }, [continueData, isDone, isInPreviewTab, preview, relatedSeries]);

  return {ticketActionInfo};
}
